import { gsap } from 'gsap'
import { SaveStateManager } from '../save-state'
import graphicCheckSvg from '../../svg/graphic-check.svg?raw'

interface ContestElements {
  container: HTMLElement
  formColumn: HTMLElement
  successColumn: HTMLElement
  endedColumn: HTMLElement
  form: HTMLFormElement
}

interface FieldRule {
  input: HTMLInputElement
  errorEl: HTMLElement
  validate: () => string | null
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function getElements(): ContestElements | null {
  const container = document.querySelector('.contest') as HTMLElement | null
  if (!container) return null

  const formColumn = container.querySelector('[data-contest-column="form"]') as HTMLElement | null
  const successColumn = container.querySelector('[data-contest-column="success"]') as HTMLElement | null
  const endedColumn = container.querySelector('[data-contest-column="ended"]') as HTMLElement | null
  const form = container.querySelector('form.form') as HTMLFormElement | null

  if (!formColumn || !successColumn || !endedColumn || !form) return null
  return { container, formColumn, successColumn, endedColumn, form }
}

function buildRules(form: HTMLFormElement): FieldRule[] {
  const name = form.querySelector<HTMLInputElement>('#name')!
  const email = form.querySelector<HTMLInputElement>('#email')!
  const termsAgreement = form.querySelector<HTMLInputElement>('#termsAgreement')!
  const personalDataAgreement = form.querySelector<HTMLInputElement>('#personalDataAgreement')!

  return [
    {
      input: name,
      errorEl: form.querySelector<HTMLElement>('#name-error')!,
      validate: () => (name.value.trim() === '' ? 'Přezdívka je povinná.' : null),
    },
    {
      input: email,
      errorEl: form.querySelector<HTMLElement>('#email-error')!,
      validate: () => {
        const v = email.value.trim()
        if (v === '') return 'E-mail je povinný.'
        if (!EMAIL_RE.test(v)) return 'Zadej platný e-mail.'
        return null
      },
    },
    {
      input: termsAgreement,
      errorEl: form.querySelector<HTMLElement>('#termsAgreement-error')!,
      validate: () => (!termsAgreement.checked ? 'Musíš souhlasit s pravidly soutěže.' : null),
    },
    {
      input: personalDataAgreement,
      errorEl: form.querySelector<HTMLElement>('#personalDataAgreement-error')!,
      validate: () =>
        !personalDataAgreement.checked ? 'Musíš vzít na vědomí informace o zpracování osobních údajů.' : null,
    },
  ]
}

function showError(rule: FieldRule, message: string): void {
  rule.errorEl.textContent = message
  rule.errorEl.classList.remove('hidden')
  rule.input.setAttribute('aria-invalid', 'true')
}

function clearError(rule: FieldRule): void {
  rule.errorEl.textContent = ''
  rule.errorEl.classList.add('hidden')
  rule.input.removeAttribute('aria-invalid')
}

function validateAll(rules: FieldRule[]): boolean {
  let firstInvalid: HTMLElement | null = null
  let valid = true

  for (const rule of rules) {
    const msg = rule.validate()
    if (msg) {
      showError(rule, msg)
      if (!firstInvalid) firstInvalid = rule.input
      valid = false
    } else {
      clearError(rule)
    }
  }

  firstInvalid?.focus()
  return valid
}

function transitionColumns(from: HTMLElement, to: HTMLElement): void {
  gsap.to(from, {
    opacity: 0,
    duration: 0.35,
    ease: 'power2.in',
    onComplete: () => {
      from.classList.add('hidden')
      to.classList.remove('hidden')
      gsap.fromTo(to, { opacity: 0 }, { opacity: 1, duration: 0.4, ease: 'power2.out' })
    },
  })
}

function sendFormDev(form: HTMLFormElement, onSuccess: () => void, onError: () => void): void {
  
  setTimeout(() => {
    const shouldSucceed = true
    if (shouldSucceed) {
      onSuccess()
    } else {
      onError()
    }
  }, 600)
}

function sendFormProd(form: HTMLFormElement, onSuccess: () => void, onError: () => void): void {
  const url = form.getAttribute('action') || '/rest/Message/send'

  window.DP!.ajax!.sendForm(form, {
    url,
    timeout: 600000,
    onSuccess: (data) => {
      if (data.status === 'OK') {
        const isDataContestNull = form.getAttribute('data-contest')
        if (isDataContestNull === null) {
          const formName = form.getAttribute('id')
          window.DP!.ifAnalytics?.((dpAnalytics) => {
            dpAnalytics.trackEvent(
              new window.DP!.analytics!.EventBuilder()
                .setEventName('funnel_step')
                .setFunnelStep(formName ?? '')
                .setInteraction(true),
            )
          })
        }
        onSuccess()
      } else {
        console.warn('Error while sending form')
        onError()
      }
    },
    onError: () => {
      console.warn('Error while sending form')
      onError()
    },
    onTimeout: () => {
      console.warn('Error while sending form')
      onError()
    },
  })
}

export function initContest(): void {
  const els = getElements()
  if (!els) return

  const { formColumn, successColumn, endedColumn, form } = els

  form.querySelectorAll<HTMLElement>('.input--checkbox').forEach((box) => {
    box.innerHTML = graphicCheckSvg
    const svg = box.querySelector('svg')
    if (svg) svg.classList.add('input__check')
  })

  const isActive = window.isContestActive !== false

  if (!isActive) {
    formColumn.classList.add('hidden')
    successColumn.classList.add('hidden')
    endedColumn.classList.remove('hidden')

    const buttonGroup = els.container.querySelector('.contest__button-group')
    if (buttonGroup) {
      const contestLink = buttonGroup.querySelector('a')
      if (contestLink) contestLink.style.display = 'none'
      const restartBtn = buttonGroup.querySelector<HTMLElement>('[data-action="restart-game"]')
      if (restartBtn) {
        restartBtn.className = 'button button--primary'
        restartBtn.style.alignSelf = 'center'
      }
    }
    return
  }

  const savedState = SaveStateManager.load()
  if (savedState?.contestSubmitted) {
    formColumn.classList.add('hidden')
    successColumn.classList.remove('hidden')
    endedColumn.classList.add('hidden')
    return
  }

  formColumn.classList.remove('hidden')
  successColumn.classList.add('hidden')
  endedColumn.classList.add('hidden')

  const nameInput = form.querySelector<HTMLInputElement>('input[name="name"]')
  if (nameInput && (window as any).userNick) {
    nameInput.value = (window as any).userNick
  }

  const rules = buildRules(form)

  for (const rule of rules) {
    rule.input.addEventListener('input', () => {
      if (rule.input.getAttribute('aria-invalid') === 'true') {
        const msg = rule.validate()
        if (!msg) clearError(rule)
      }
    })
    rule.input.addEventListener('change', () => {
      if (rule.input.getAttribute('aria-invalid') === 'true') {
        const msg = rule.validate()
        if (!msg) clearError(rule)
      }
    })
  }

  form.addEventListener('submit', (e) => {
    e.preventDefault()
    if (!validateAll(rules)) return

    const submitBtn = form.querySelector<HTMLButtonElement>('button[type="submit"]')
    if (submitBtn) {
      submitBtn.disabled = true
      submitBtn.setAttribute('aria-busy', 'true')
    }

    const onSuccess = () => {
      const state = SaveStateManager.load() ?? SaveStateManager.createInitialState()
      state.contestSubmitted = true
      SaveStateManager.saveImmediate(state)
      transitionColumns(formColumn, successColumn)
    }

    const onError = () => {
      if (submitBtn) {
        submitBtn.disabled = false
        submitBtn.removeAttribute('aria-busy')
      }
    }

    if (import.meta.env.DEV) {
      sendFormDev(form, onSuccess, onError)
    } else {
      sendFormProd(form, onSuccess, onError)
    }
  })
}
